import {
  CollideHandler,
  ICollision,
} from '../../actions/collisions/ICollision';
import { PositionType } from '../../../lib/defs/types';
import { CollideEvent, CollideId, TapCollider } from '../../defs/collide';
import { despawnBlockEntity } from '../../entities/BlockEntity';
import { GameScene } from '../../GameScene';
import { GrassBlockEffect } from './GrassBlockEffect';
import { GrassBlockEntity } from './GrassBlockEntity';

const surroundingPositionDiffs: PositionType[] = [
  { x: -1, y: -1 },
  { x: 0, y: -1 },
  { x: 1, y: -1 },
  { x: -1, y: 0 },
  { x: 1, y: 0 },
  { x: -1, y: 1 },
  { x: 0, y: 1 },
  { x: 1, y: 1 },
];

export class GrassBlockCollision implements ICollision {
  private readonly scene: GameScene;
  private readonly entity: GrassBlockEntity;
  private readonly resolvers: { [key in CollideId]?: CollideHandler } = {
    tap: this.resolveTap,
    attack: this.resolveAttack,
    block: this.resolveBlock,
  };

  constructor(scene: GameScene, entity: GrassBlockEntity) {
    this.scene = scene;
    this.entity = entity;
  }

  public async resolve(event: CollideEvent): Promise<boolean> {
    return this.resolvers[event.id]?.call(this, event) ?? false;
  }

  private async resolveTap(event: CollideEvent) {
    const cell = (event.collider as TapCollider).cell;
    const layer = cell.base;

    layer?.entity.c.block.collide('tap', { type: 'tap', cell });
    return false;
  }

  private async resolveAttack() {
    this.attack();
    return true;
  }

  private async resolveBlock(event: CollideEvent) {
    const collider = event.collider;
    if (
      collider?.type === 'block' &&
      collider.entity.c.block.props.type === 'basic'
    ) {
      this.attack();
      return true;
    }
    return false;
  }

  private attack() {
    this.shakeSurroundingGrassBlocks();
    const hasMushrooms = this.entity.c.blockGrass.view.count > 1;
    void new GrassBlockEffect(this.scene, this.entity).playHitParticles(
      hasMushrooms,
    );
    const cleared = this.entity.c.blockGrass.hit();
    if (cleared) {
      despawnBlockEntity(this.scene, this.entity);
    }
  }

  private shakeSurroundingGrassBlocks() {
    for (const positionDiff of surroundingPositionDiffs) {
      this.shakeGrassBlockAt(positionDiff);
    }
  }

  private shakeGrassBlockAt(positionDiff: PositionType) {
    const mapComponent = this.scene.sessionEntity.c.map;
    const position = this.entity.c.position.mapPosition;
    const cell = mapComponent.getCellAt({
      x: position.x + positionDiff.x,
      y: position.y + positionDiff.y,
    });

    const overlayEntity = cell?.overlay?.entity;
    if (overlayEntity?.c.block.blockId !== 'grass') return;
    if (!overlayEntity.c.view2d) return;
    if (overlayEntity.c.view2d.view.alpha < 1) return;

    (overlayEntity as GrassBlockEntity).c.blockGrass.view.playShakeAnimation();
  }
}
